import { useState, useEffect, useCallback } from "react";
import { 
  Bell, 
  User, 
  ChevronDown, 
  Search, 
  Settings, 
  LogOut, 
  Command,
  Sun,
  Moon,
  Monitor,
  HelpCircle,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuShortcut,
} from "@/components/ui/dropdown-menu";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import ProfileDetailCard from "../profile/ProfileCard";
import api, { MEDIA_URL, userApi } from "@/services/api";

type Profile = {
  name: string;
  email: string;
  image: string;
};

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [userName, setUserName] = useState<string>("User");
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system');
  const [notifications] = useState([
    { id: 1, title: "New payment received", time: "2 min ago", unread: true },
    { id: 2, title: "Collection updated", time: "1 hour ago", unread: true },
    { id: 3, title: "Monthly report ready", time: "3 hours ago", unread: false },
  ]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [profile, setProfile] = useState<Profile>({
    name: "",
    email: "",
    image: "",
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.authenticated.getProfile();
        setProfile(response.data);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
  }, []);

  // Keyboard shortcuts
  const handleKeyboardShortcuts = useCallback((event: KeyboardEvent) => {
    // Cmd/Ctrl + K for search
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      }
    }
    
    // Cmd/Ctrl + Shift + P for profile
    if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'P') {
      event.preventDefault();
      setIsProfileOpen(true);
    }
    
    // Escape to close search
    if (event.key === 'Escape' && isSearchFocused) {
      setSearchQuery('');
      (document.activeElement as HTMLElement)?.blur();
    }
  }, [isSearchFocused]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcuts);
    return () => document.removeEventListener('keydown', handleKeyboardShortcuts);
  }, [handleKeyboardShortcuts]);

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        // First try to get from localStorage
        const profileData = localStorage.getItem("manterflow-user-profile");

        if (profileData) {
          const { name } = JSON.parse(profileData);
          setUserName(name);
        } else {
          // If not in localStorage, get user ID from manterflow-user
          const userData = localStorage.getItem("manterflow-user");
          if (userData) {
            const { id } = JSON.parse(userData);
            // Fetch user data from API
            const response = await userApi.getUser(id);
            setUserName(response.data.name);
          }
        }
      } catch (error) {
        console.error("Error fetching user name:", error);
      }
    };

    fetchUserName();
  }, []);

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
    logout();
    navigate("/login");
  };

  const handleProfileClick = () => {
    setIsProfileOpen(true);
  };

  const toggleTheme = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    // In a real app, you'd apply the theme here
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const unreadNotifications = notifications.filter(n => n.unread).length;

  // Get page title from current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path.startsWith('/collection')) return 'Collection Management';
    if (path.startsWith('/users')) return 'User Management';
    if (path.startsWith('/reports')) return 'Reports & Analytics';
    if (path.startsWith('/masters')) return 'Master Data';
    return 'ManterFlow';
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-slate-900/60">
        <div className="flex h-16 items-center justify-between gap-4 px-4 lg:px-6">
          {/* Left side - Page Title */}
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="hidden md:block text-lg font-semibold text-slate-900 dark:text-slate-100 tracking-tight truncate">
              {getPageTitle()}
            </h1>
          </div>

          {/* Center - Enhanced Global Search */}
          {/* <div className="flex-1 max-w-md mx-4">
            <div className="relative">
              <Search className={cn(
                "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 transition-colors duration-200",
                isSearchFocused ? "text-blue-500" : "text-slate-400"
              )} />
              <Input
                type="search"
                placeholder="Search everything... (⌘K)"
                className={cn(
                  "pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700",
                  "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200",
                  "placeholder:text-slate-400"
                )}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 text-slate-400 hover:text-slate-600"
                >
                  <Command className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div> */}

          {/* Right side - Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Theme Toggle */}
            <TooltipProvider>
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-9 w-9">
                        <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                        <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                        <span className="sr-only">Toggle theme</span>
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Toggle theme</p>
                  </TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => toggleTheme('light')}>
                    <Sun className="mr-2 h-4 w-4" />
                    Light
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => toggleTheme('dark')}>
                    <Moon className="mr-2 h-4 w-4" />
                    Dark
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => toggleTheme('system')}>
                    <Monitor className="mr-2 h-4 w-4" />
                    System
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TooltipProvider>

            {/* Notifications */}
            {/* <TooltipProvider>
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="relative h-9 w-9">
                        <Bell className="h-4 w-4" />
                        {unreadNotifications > 0 && (
                          <Badge className="absolute -right-1 -top-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
                            {unreadNotifications}
                          </Badge>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Notifications</p>
                  </TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    Notifications
                    {unreadNotifications > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {unreadNotifications} new
                      </Badge>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifications.map((notification) => (
                    <DropdownMenuItem key={notification.id} className="flex flex-col items-start p-3">
                      <div className="flex items-center justify-between w-full">
                        <span className={cn(
                          "text-sm",
                          notification.unread ? "font-semibold" : "font-normal"
                        )}>
                          {notification.title}
                        </span>
                        {notification.unread && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                      </div>
                      <span className="text-xs text-slate-500 mt-1">{notification.time}</span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-center text-sm text-blue-600 dark:text-blue-400">
                    View all notifications
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TooltipProvider> */}

            {/* Help */}
            {/* <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9">
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Help & Support</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider> */}

            {/* User Profile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-3 h-9 px-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={MEDIA_URL+profile?.image || ""} alt={profile?.name || "User"} />
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xs font-semibold">
                      {profile?.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline-flex text-sm font-medium text-slate-700 dark:text-slate-300">
                    {profile?.name || "User"}
                  </span>
                  <ChevronDown className="h-3 w-3 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={MEDIA_URL+profile?.image || ""} alt={profile?.name || "User"} />
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm font-semibold">
                      {profile?.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">{profile?.name || "User"}</span>
                    <span className="text-xs text-slate-500">{profile?.email || 'user@example.com'}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleProfileClick}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                {/* <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  Preferences
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Zap className="mr-2 h-4 w-4" />
                  Upgrade Plan
                </DropdownMenuItem> */}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
        <DialogContent className="max-w-2xl p-0 sm:p-6">
          <ProfileDetailCard onClose={() => setIsProfileOpen(false)} />
        </DialogContent>
      </Dialog>

      <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Are you sure you want to logout?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You will need to login again to access your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLogout}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
